import { ErrorHandlerResult, UnknownError } from '@/api/generator';
import { notificationForError, notificationForDeviceError } from '@/components/notifications/utils';
import { routenames } from '@/router/routenames.ts';
import { routerInstance } from '@/router/router.ts';
import { getRedirectPathFromRoute } from '@/router/utils';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { useSystemStore } from '@/stores/systemStore';
import { VeeFormContext } from '@/components/forms/useVeeForm';
import {
  AnyApiError,
  ApiError,
  assertNever,
  isApiFailure,
  isMaintenanceModeError,
} from './typeGuards';
import { errorFlowAsync } from './handle';
import { Router } from 'vue-router';
import { SKIP_FORM_CHECK } from '@/components/confirmationModals/useFormConfirmationModal';
import { mapBackendErrorToAjvErrors } from '@/validation/validators/backendValidator';
import { Level, trackException } from '@/telemetry/appInsights';

/**
 * Stop the error flow with this handler, only do this if you're sure you handle all relevant errors before using other handlers or it might hide errors
 */
export function stopErrorPropagation() {
  return undefined;
}

/**
 * Marks intentionally superseded API cancellations so the default handler can ignore them safely.
 */
function isAbortError(error: unknown) {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const name = 'name' in error ? String(error.name) : undefined;
  if (name === 'AbortError') {
    return true;
  }

  const cause = 'cause' in error ? error.cause : undefined;
  if (!cause || cause === error || typeof cause !== 'object') {
    return false;
  }

  const causeName = 'name' in cause ? String(cause.name) : undefined;
  return causeName === 'AbortError';
}

export function ignoreCancellationErrorHandler(error: AnyApiError) {
  // Abort can surface either as FetchError(cause = AbortError) or as a direct AbortError
  // depending on browser/runtime timing. Treat both as intentional cancellation.
  if (isAbortError(error)) {
    return undefined;
  }

  return error;
}

export async function logConsoleErrorHandler(error: AnyApiError) {
  console.error(error);
  return error;
}

async function signOutErrorHandler(error: AnyApiError) {
  routerInstance.push({
    name: routenames.signOut,
    hash: `#${SKIP_FORM_CHECK}`,
    query: { redirect: getRedirectPathFromRoute(routerInstance.currentRoute.value) },
  });
  return error;
}

async function notifyErrorHandler(error: AnyApiError) {
  useNotificationStore().notify(notificationForError(error), { store: true });
  return error;
}

/**
 * Returns `error` rather than suppressing it directly - callers append `stopErrorPropagation`
 * to their handler list to actually stop the chain; omitting it propagates to `defaultErrorHandler`.
 */
export async function catchAllErrorHandler(error: AnyApiError) {
  const notificationEntry = notificationForError(error);
  useNotificationStore().addMessage(notificationEntry);
  return error;
}

async function updatePermissionsErrorHandler(error: AnyApiError) {
  await usePermissionsStore().fetchMyPermissions();
  return error;
}

/** 400 BadRequest with validationErrors array populated */
export function hasValidationErrors(error: AnyApiError) {
  return error.name === ApiError.badRequest && !!error.error?.exception?.validationErrors?.length;
}

export function ignoreValidationErrorHandler(error: AnyApiError) {
  return hasValidationErrors(error) ? undefined : error;
}

/**
 * Factory function for error handler that passes error information to form to be shown at the field for the path in the validated object of type T
 */
export function formBackendErrorHandler(form: VeeFormContext, propagateValidationErrors = false) {
  return (error: AnyApiError) => {
    const aggregatedErrors = mapBackendErrorToAjvErrors(error);

    if (aggregatedErrors?.length) {
      aggregatedErrors?.forEach((aggregatedError) => {
        form.setFieldError(aggregatedError.path, aggregatedError.errors[0]);
      });

      return propagateValidationErrors ? error : undefined;
    } else {
      return error;
    }
  };
}

export function entityNotFoundErrorHandler(router: Router) {
  return async (error: AnyApiError) => {
    if (
      error.name === ApiError.notFound ||
      error.name === ApiError.badRequest ||
      error.name === ApiError.unprocessableContent
    ) {
      await router.push({ name: routenames.notFound, hash: `#${SKIP_FORM_CHECK}` });
    } else {
      return error;
    }
  };
}

/**
 * Maintenance mode confirmed by the body's `$type`. Returns nothing on purpose: this is a
 * redirect flow, so the handler chain stops here instead of logging, notifying and tracking.
 */
async function maintenanceModeErrorHandler(_error: AnyApiError) {
  const systemStore = useSystemStore();
  await systemStore.fetchMaintenanceModeInfo();
  await routerInstance.push({ name: routenames.signOut });
}

function trackErrorHandler(error: AnyApiError) {
  trackException(
    error,
    {
      detail: error.name,
    },
    Level.Error,
  );
  return error;
}

/**
 * Renders the `DeviceMeasurementErrorDto` payload (id/severity/cause/remedy) through the same
 * formatter `useMeasurement.ts` uses for in-band measurement errors. The generic
 * i18n-key/message notification cannot surface those fields.
 */
async function notifyDeviceMeasurementErrorHandler(error: AnyApiError) {
  const deviceError =
    error.name === ApiError.deviceMeasurement ? error.error?.exception?.error : undefined;

  useNotificationStore().notify(
    deviceError ? notificationForDeviceError(deviceError) : notificationForError(error),
    { store: true },
  );
  return error;
}

/**
 * Default handler chain per error name. Exhaustive over every known API error name plus
 * `FetchError`/`UnknownError`/`ResponseError`, so adding a backend exception type fails the
 * build until someone gives it a case and decides its handling.
 *
 * Takes the wide `Error` type because this is the chain's outermost boundary and also accepts
 * input `handle()` never produces, such as direct test calls.
 */
export function defaultErrorHandler(error: AnyApiError): ErrorHandlerResult {
  if (!isApiFailure(error)) {
    // Normalizing here keeps every handler below typed as `AnyApiError` instead of `Error`.
    return errorFlowAsync(
      logConsoleErrorHandler,
      notifyErrorHandler,
      trackErrorHandler,
    )(new UnknownError(error));
  }

  switch (error.name) {
    case ApiError.unauthorized:
      return errorFlowAsync(
        logConsoleErrorHandler,
        notifyErrorHandler,
        signOutErrorHandler,
        trackErrorHandler,
      )(error);

    case ApiError.conflict:
      return errorFlowAsync(logConsoleErrorHandler, trackErrorHandler)(error);

    // Both exception types are HTTP 403 and an operation's spec can declare only one, so
    // `.name` alone cannot tell them apart - the body's `$type` (checked via
    // `isMaintenanceModeError`) is the real discriminator between the two branches.
    case ApiError.forbidden:
    case ApiError.maintenanceMode:
      return isMaintenanceModeError(error)
        ? errorFlowAsync(maintenanceModeErrorHandler)(error)
        : errorFlowAsync(
            logConsoleErrorHandler,
            notifyErrorHandler,
            updatePermissionsErrorHandler,
            trackErrorHandler,
          )(error);

    // `csoPortalBadGateway` and `csoPortalServiceUnavailable` reach this chain only when
    // `qaEquipmentStore`, which renders their remedy, did not consume them first.
    case ApiError.badRequest:
    case ApiError.notFound:
    case ApiError.unprocessableContent:
    case ApiError.internalServer:
    case ApiError.serviceUnavailable:
    case ApiError.badGateway:
    case 'FetchError':
    case 'UnknownError':
    case 'ResponseError':
    case ApiError.setupCombinations:
    case ApiError.csoPortalBadGateway:
    case ApiError.csoPortalServiceUnavailable:
      return errorFlowAsync(logConsoleErrorHandler, notifyErrorHandler, trackErrorHandler)(error);

    // Carries a DeviceMeasurementErrorDto payload the generic notification cannot render.
    case ApiError.deviceMeasurement:
      return errorFlowAsync(
        logConsoleErrorHandler,
        notifyDeviceMeasurementErrorHandler,
        trackErrorHandler,
      )(error);

    default:
      return assertNever(error);
  }
}
